/**
 * Écran client — page plein écran affichée sur le 2e moniteur (HDMI 7", 1024×600)
 * face au client, à la caisse.
 *
 * Reçoit l'état du POS via BroadcastChannel (même machine, aucune requête API,
 * fonctionne hors-ligne). V1 : montant + panier + poids balance — pas de paiement, pas de QR.
 *
 * Ouverture : 2e fenêtre Safari sur https://foliesboutik.be/pos/affichage-client,
 * glissée sur le moniteur client puis passée en plein écran.
 */
import { useEffect, useRef, useState } from 'react';
import {
  CUSTOMER_DISPLAY_CHANNEL,
  CustomerDisplayMessage,
  CustomerDisplayScale,
  CustomerDisplayState,
} from '../../services/customerDisplayChannel';
import { formatPrice, formatQty } from '../../utils/pos.utils';

/** Poids net en kg, format balance (3 décimales, virgule) */
function formatWeightKg(grams: number): string {
  return (grams / 1000).toFixed(3).replace('.', ',') + ' kg';
}

/** Quantité avec virgule décimale (affichage client belge) */
function formatQtyFr(qty: number): string {
  return formatQty(qty).replace('.', ',');
}

// Ecran 7 pouces, 1024x600, lu a un metre : le texte prime sur le nombre de
// lignes. Le client verifie le dernier article et son total, il ne relit pas
// tout son panier — le detail est sur le ticket.
const MAX_VISIBLE_LINES = 4;
const THANKS_DURATION_MS = 8000;
/** En-dessous de ce poids (g) on considère la balance vide (bruit) */
const WEIGHT_NOISE_GRAMS = 5;
/** Sans activité (panier ou balance) pendant ce délai, retour à l'écran d'accueil bio.
 *  Le moindre article encodé ou mouvement de balance ramène le panier instantanément.
 *  Jamais pendant un encaissement (payment_in_progress). */
const IDLE_AFTER_INACTIVE_MS = 10_000;

export default function CustomerDisplayPage() {
  const [displayState, setDisplayState] = useState<CustomerDisplayState | null>(null);
  const [scaleOverride, setScaleOverride] = useState<CustomerDisplayScale | null>(null);
  const [thanks, setThanks] = useState<{ total_ttc_cents: number } | null>(null);
  const thanksTimerRef = useRef<number>();

  // Veille d'inactivité : horodatage de la dernière activité (panier modifié / balance
  // en mouvement) + tick chaque seconde pour réévaluer le délai
  const lastActivityRef = useRef<number>(Date.now());
  const lastCartSignatureRef = useRef<string>('');
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(CUSTOMER_DISPLAY_CHANNEL);

    channel.onmessage = (event: MessageEvent<CustomerDisplayMessage>) => {
      const msg = event.data;
      if (!msg?.type) return;

      if (msg.type === 'state') {
        // Le panier a-t-il réellement changé ? (le 'state' repart aussi sur des
        // changements sans intérêt pour la veille, ex. reprise de lock)
        const signature = JSON.stringify(msg.payload.lines) + '|' + msg.payload.total_ttc_cents;
        if (signature !== lastCartSignatureRef.current) {
          lastCartSignatureRef.current = signature;
          lastActivityRef.current = Date.now();
        }
        setDisplayState(msg.payload);
        setScaleOverride(null);
        // Un nouvel article encodé pendant l'écran « Merci » = nouvelle vente → revenir au panier
        if (msg.payload.lines.length > 0) {
          window.clearTimeout(thanksTimerRef.current);
          setThanks(null);
        }
      } else if (msg.type === 'scale') {
        // 'reading' = le poids bouge (quelque chose est posé/retiré) → activité
        if (msg.payload.status === 'reading') {
          lastActivityRef.current = Date.now();
        }
        setScaleOverride(msg.payload);
      } else if (msg.type === 'sale_complete') {
        setThanks({ total_ttc_cents: msg.payload.total_ttc_cents });
        window.clearTimeout(thanksTimerRef.current);
        thanksTimerRef.current = window.setTimeout(() => setThanks(null), THANKS_DURATION_MS);
      }
    };

    // Demander l'état courant au POS (utile si l'écran s'ouvre après le POS)
    channel.postMessage({ type: 'hello' } satisfies CustomerDisplayMessage);

    return () => {
      window.clearTimeout(thanksTimerRef.current);
      channel.close();
    };
  }, []);

  const scale = scaleOverride ?? displayState?.scale ?? null;
  // Session inactive (vendeur parti sur un autre poste / déconnecté) → accueil,
  // même si le panier local du POS n'est pas vide
  const sessionActive = displayState?.session_active !== false;
  const lines = sessionActive ? displayState?.lines ?? [] : [];
  const hasWeight = !!scale?.connected && scale.weight_grams > WEIGHT_NOISE_GRAMS;

  // Veille d'inactivité : panier immobile ET balance immobile depuis 10 s → accueil bio,
  // SAUF encaissement en cours (le client doit voir son total) et SAUF quelque chose
  // posé sur la balance (poids > bruit = pesée en cours, l'écran reste utile).
  // nowTick force la réévaluation chaque seconde.
  void nowTick;
  // La confirmation FOLIES vaut encaissement en cours, même si le dialogue
  // principal de paiement n'est pas (ou plus) ouvert : sans ça l'écran client
  // pouvait partir en veille juste au moment où le client doit voir le montant.
  const paymentInProgress = !!displayState?.payment_in_progress || !!displayState?.wallet_payment;
  const inactive =
    !paymentInProgress &&
    !(hasWeight && sessionActive) &&
    Date.now() - lastActivityRef.current > IDLE_AFTER_INACTIVE_MS;

  // 'payment' : le dialogue d'encaissement est ouvert → le client n'a plus qu'une
  // question, « combien ? ». Le détail des articles ne lui sert plus, et il est
  // souvent lu de loin, au moment de sortir la carte : le total passe donc en
  // grand, seul, au centre.
  // 'weighing' : panier vide mais un article est posé sur la balance → poids en grand
  const phase: 'thanks' | 'payment' | 'sale' | 'weighing' | 'idle' =
    thanks ? 'thanks'
    : paymentInProgress && lines.length > 0 ? 'payment'
    : lines.length > 0 && !inactive ? 'sale'
    : sessionActive && hasWeight ? 'weighing'
    : 'idle';

  return (
    <div className="w-screen h-screen overflow-hidden select-none cursor-none bg-gradient-to-br from-[#1b4332] via-[#2d6a4f] to-[#1b4332] text-white flex flex-col">
      {/* En-tête */}
      <header className="flex items-center justify-between px-6 py-3 bg-black/20">
        <div className="flex items-center gap-3">
          <img src="/logo-folies.png" alt="" className="h-10 w-10 rounded-md object-contain" />
          <div>
            <div className="text-xl font-bold leading-tight">Les Folies Maraîchères</div>
            <div className="text-xs text-emerald-200">Producteurs bio — Nature &amp; Progrès</div>
          </div>
        </div>
        {displayState?.vendor_name && sessionActive && (
          <div className="text-sm text-emerald-200">
            Vous êtes servi·e par <span className="font-semibold text-white">{displayState.vendor_name}</span>
          </div>
        )}
      </header>

      {phase === 'thanks' && thanks && (
        <main className="flex-1 flex flex-col items-center justify-center gap-6">
          <div className="text-6xl">🌱</div>
          <div className="text-5xl font-bold">Merci de votre visite !</div>
          <div className="text-3xl text-emerald-200">
            Total payé&nbsp;: <span className="font-bold text-white tabular-nums">{formatPrice(thanks.total_ttc_cents)}</span>
          </div>
          <div className="text-xl text-emerald-300">À très bientôt 👋</div>
        </main>
      )}

      {phase === 'idle' && (
        <main className="flex-1 flex flex-col items-center justify-center gap-6">
          <div className="text-5xl font-bold text-center px-4">Bienvenue aux Folies Maraîchères</div>
          <div className="flex items-center gap-8">
            <img src="/logo-folies.png" alt="Les Folies Maraîchères" className="h-36 w-36 rounded-2xl object-contain shadow-2xl" />
            <img src="/logo-np.png" alt="Producteur bio Nature & Progrès" className="h-36 w-36 rounded-full bg-white p-2 object-contain shadow-2xl" />
            <img src="/logo-eu-bio.jpg" alt="Agriculture biologique — logo européen" className="h-36 rounded-2xl shadow-2xl" />
          </div>
          <div className="text-3xl font-semibold text-emerald-100 text-center max-w-3xl px-8 leading-snug">
            100% de nos produits sont issus de l'agriculture biologique
          </div>
        </main>
      )}

      {phase === 'weighing' && scale && (
        <main className="flex-1 flex flex-col items-center justify-center gap-5">
          <div className="text-2xl uppercase tracking-widest text-emerald-300">⚖️ Poids</div>
          <div
            className={`px-14 py-8 rounded-3xl transition-colors ${
              scale.status === 'stable' ? 'bg-emerald-500/25 ring-4 ring-emerald-300' : 'bg-black/30'
            }`}
          >
            <div className="text-8xl font-bold tabular-nums">{formatWeightKg(scale.weight_grams)}</div>
          </div>
        </main>
      )}

      {phase === 'payment' && displayState && !displayState.wallet_payment && (
        <main className="flex-1 flex flex-col items-center justify-center gap-4">
          {/* Retrait d'une commande en ligne : le client a déjà payé l'essentiel.
              Lui montrer le total du panier (10/10/2026) lui faisait croire qu'on
              lui réclamait sa commande une seconde fois. */}
          {displayState.online_order ? (
            <>
              <div className="text-2xl uppercase tracking-[0.3em] text-emerald-300">À payer aujourd’hui</div>
              <div className="font-bold tabular-nums leading-none text-[10rem]">
                {formatPrice(displayState.online_order.due_today_cents)}
              </div>
              <div className="text-xl text-emerald-200">
                Commande en ligne déjà payée&nbsp;: {formatPrice(displayState.online_order.already_paid_cents)}
              </div>
            </>
          ) : (
            <>
              <div className="text-2xl uppercase tracking-[0.3em] text-emerald-300">À payer</div>
              <div className="font-bold tabular-nums leading-none text-[10rem]">
                {formatPrice(displayState.total_ttc_cents)}
              </div>
              <div className="text-xl text-emerald-200">
                {lines.length} article{lines.length > 1 ? 's' : ''} · TVA comprise
              </div>
            </>
          )}
        </main>
      )}

      {/* Paiement sur le compte FOLIES : le client ne tape ni code ni signature,
          il doit donc VOIR ce qu'on s'apprête à prendre — et son solde après —
          pendant que le vendeur a la confirmation sous les yeux. */}
      {phase === 'payment' && displayState?.wallet_payment && (
        <main className="flex-1 flex flex-col items-center justify-center gap-3">
          <div className="text-2xl uppercase tracking-[0.3em] text-emerald-300">Paiement avec vos FOLIES</div>
          <div className="font-bold tabular-nums leading-none text-[9rem]">
            {formatPrice(displayState.wallet_payment.amount_cents)}
          </div>
          <div className="mt-2 rounded-2xl bg-white/10 px-10 py-4 text-center">
            <div className="text-lg uppercase tracking-widest text-emerald-300">Solde après paiement</div>
            <div className="text-5xl font-bold tabular-nums">
              {formatPrice(displayState.wallet_payment.balance_after_cents)}
            </div>
          </div>
          <div className="text-lg text-emerald-200">Prévenez-nous si ce montant ne vous convient pas</div>
        </main>
      )}

      {phase === 'sale' && displayState && (
        <main className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 flex min-h-0">
            {/* Colonne gauche : article qui vient d'être ajouté (vedette) + précédents */}
            <section className="flex-1 min-w-0 flex flex-col p-4 gap-2">
              {lines.length > 0 && (() => {
                const last = lines[lines.length - 1];
                return (
                  <div className="rounded-2xl bg-white/15 ring-1 ring-white/30 px-6 py-4 flex items-end justify-between gap-4">
                    {/* Le prix vedette s'aligne à DROITE comme celui des lignes
                        suivantes : une colonne de prix se lit d'un seul regard,
                        deux alignements obligent l'œil à faire l'aller-retour. */}
                    <div className="min-w-0 flex-1">
                    <div className="truncate font-semibold text-4xl">
                      {last.name}
                      {last.is_deposit && <span className="ml-2 text-base font-normal text-amber-300">consigne</span>}
                      {last.is_return && !last.is_refund && <span className="ml-2 text-base font-normal text-emerald-300">retour</span>}
                      {last.is_refund && <span className="ml-2 text-base font-normal text-orange-300">remboursement</span>}
                    </div>
                    <div className="tabular-nums text-2xl text-emerald-200 mt-1">
                      {last.is_weighted
                        ? `${formatPrice(last.unit_price_cents)}/kg × ${formatQtyFr(last.qty)} kg`
                        : `${formatPrice(last.unit_price_cents)} × ${formatQtyFr(last.qty)}`}
                    </div>
                    {/* « De préférence avant le » : c'est une DDM, pas une DLC —
                        un œuf reste consommable après la date. Même formulation
                        que le ticket, pour ne pas dire deux choses différentes. */}
                    {last.ddm && (
                      <div className="text-xl text-amber-200 mt-1">
                        De préférence avant le {new Date(last.ddm).toLocaleDateString('fr-BE')}
                      </div>
                    )}
                    </div>
                    {/* Prix de l'article — l'information vedette */}
                    <div className="font-bold tabular-nums text-7xl leading-none whitespace-nowrap text-right">
                      {formatPrice(last.line_total_cents)}
                    </div>
                  </div>
                );
              })()}
              {/* Articles précédents, compacts */}
              {[...lines].reverse().slice(1, MAX_VISIBLE_LINES).map((line, idx) => (
                <div
                  key={`${lines.length}-${idx}`}
                  className="flex items-center gap-3 rounded-xl px-4 py-1.5 bg-white/5"
                >
                  <div className="flex-1 min-w-0 truncate text-2xl text-emerald-100">
                    {line.name}
                    {line.ddm && (
                      <span className="ml-2 text-lg text-amber-200">
                        avant le {new Date(line.ddm).toLocaleDateString('fr-BE')}
                      </span>
                    )}
                    {line.is_deposit && <span className="ml-2 text-sm font-normal text-amber-300">consigne</span>}
                    {line.is_return && !line.is_refund && <span className="ml-2 text-sm font-normal text-emerald-300">retour</span>}
                    {line.is_refund && <span className="ml-2 text-sm font-normal text-orange-300">remboursement</span>}
                  </div>
                  <div className="font-bold tabular-nums whitespace-nowrap text-2xl text-emerald-100">
                    {formatPrice(line.line_total_cents)}
                  </div>
                </div>
              ))}
              {lines.length > MAX_VISIBLE_LINES && (
                <div className="text-center text-emerald-300 text-lg">
                  + {lines.length - MAX_VISIBLE_LINES} autre{lines.length - MAX_VISIBLE_LINES > 1 ? 's' : ''} article{lines.length - MAX_VISIBLE_LINES > 1 ? 's' : ''}
                </div>
              )}
            </section>

            {/* Colonne droite : poids en grand (seulement si un poids est posé) */}
            {hasWeight && scale && (
              <aside className="w-[400px] flex flex-col p-4">
                <div
                  className={`flex-1 rounded-2xl px-6 py-4 flex flex-col items-center justify-center gap-2 transition-colors ${
                    scale.status === 'stable' ? 'bg-emerald-500/25 ring-2 ring-emerald-300' : 'bg-black/30'
                  }`}
                >
                  <div className="text-lg uppercase tracking-widest text-emerald-300">⚖️ Poids</div>
                  <div className="text-7xl font-bold tabular-nums">{formatWeightKg(scale.weight_grams)}</div>
                </div>
              </aside>
            )}
          </div>

          {/* Bandeau total — tout en bas, pleine largeur */}
          <footer className="flex items-center justify-between px-8 py-3 bg-black/40">
            <div className="text-2xl uppercase tracking-widest text-emerald-300">
              Total ({lines.length} article{lines.length > 1 ? 's' : ''})
              <span className="ml-3 normal-case tracking-normal text-sm text-emerald-400">TVA comprise</span>
            </div>
            {displayState.online_order ? (
              <div className="flex items-baseline gap-6">
                <div className="text-lg text-emerald-200">
                  Déjà payé en ligne&nbsp;: <span className="tabular-nums">{formatPrice(displayState.online_order.already_paid_cents)}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm uppercase tracking-widest text-emerald-300">À payer</div>
                  <div className="text-5xl font-bold tabular-nums">{formatPrice(displayState.online_order.due_today_cents)}</div>
                </div>
              </div>
            ) : (
              <div className="text-5xl font-bold tabular-nums">{formatPrice(displayState.total_ttc_cents)}</div>
            )}
          </footer>
        </main>
      )}
    </div>
  );
}
